/**
 * REDEX Stratagem Nexus - Command Center UI
 * 
 * Autonomous vertical discovery, arbitrage, M&A ingestion,
 * authority loops, and shadow deployments.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Button,
  TextField,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Badge,
  Avatar,
  Divider,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Radar as RadarIcon,
  TrendingUp as TrendingUpIcon,
  Merge as MergeIcon,
  Link as LinkIcon,
  Cloud as CloudIcon,
  Security as SecurityIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Science as ScienceIcon,
  Rocket as RocketIcon,
  Psychology as PsychologyIcon,
  Hub as HubIcon,
  AutoGraph as AutoGraphIcon,
  Visibility as VisibilityIcon,
  Analytics as AnalyticsIcon,
  Speed as SpeedIcon,
  AccountTree as AccountTreeIcon,
  BubbleChart as BubbleChartIcon,
} from '@mui/icons-material';
import stratagemService, {
  StratagemDashboard,
  VerticalScore,
  IntentFlow,
  AcquisitionTarget,
  ShadowInstance,
  AnomalyAlert,
  Playbook,
} from '../services/stratagemService';
import FederalCaptureTab from '../components/stratagem/FederalCaptureTab';
import SalesFlywheelTab from '../components/stratagem/SalesFlywheelTab';
import { MonetizationOn as FlywheelIcon } from '@mui/icons-material';

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 16 }}>
      {value === index && children}
    </div>
  );
}

// Status Badge Component
const StatusBadge: React.FC<{ status: string; size?: 'small' | 'medium' }> = ({ status, size = 'small' }) => {
  const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
    // Vertical status
    discovered: 'info',
    sandbox: 'warning',
    promoted: 'success',
    rejected: 'error',
    // Instance status
    provisioning: 'info',
    active: 'success',
    paused: 'warning',
    archived: 'default',
    // Target status
    identified: 'info',
    analyzing: 'warning',
    approved: 'success',
    ingesting: 'warning',
    complete: 'success',
    // Alert severity
    low: 'default',
    medium: 'info',
    high: 'warning',
    critical: 'error',
  };
  
  return (
    <Chip
      label={status.toUpperCase()}
      color={colorMap[status] || 'default'}
      size={size}
      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
    />
  );
};

// VAS Score Display
const VasScore: React.FC<{ score: number }> = ({ score }) => {
  const getColor = (s: number) => {
    if (s >= 0.8) return 'success.main';
    if (s >= 0.6) return 'warning.main';
    if (s >= 0.4) return 'info.main';
    return 'error.main';
  };
  
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <CircularProgress
        variant="determinate"
        value={score * 100}
        size={36}
        sx={{ color: getColor(score) }}
      />
      <Typography variant="body2" fontWeight={600}>
        {(score * 100).toFixed(0)}
      </Typography>
    </Box>
  );
};

// Stats Card Component
const StatsCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: string | number;
  subtitle?: string;
  color?: string;
  trend?: number;
}> = ({ icon, title, value, subtitle, color = 'primary.main', trend }) => {
  const theme = useTheme();
  
  return (
    <Card sx={{ height: '100%', background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color, mr: 1.5 }}>
            {icon}
          </Avatar>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" fontWeight={700} color={color}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {trend !== undefined && (
          <Chip
            size="small"
            label={`${trend > 0 ? '+' : ''}${trend}%`}
            color={trend > 0 ? 'success' : 'error'}
            sx={{ mt: 1, fontSize: '0.7rem' }}
          />
        )}
      </CardContent>
    </Card>
  );
};

// Main Component
const StratagemNexus: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState<StratagemDashboard | null>(null);
  
  // Module-specific state
  const [verticalScores, setVerticalScores] = useState<VerticalScore[]>([]);
  const [intentFlows, setIntentFlows] = useState<IntentFlow[]>([]);
  const [targets, setTargets] = useState<AcquisitionTarget[]>([]);
  const [instances, setInstances] = useState<ShadowInstance[]>([]);
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  const [alerts, setAlerts] = useState<AnomalyAlert[]>([]);
  
  // Dialogs
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [instanceDialogOpen, setInstanceDialogOpen] = useState(false);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [playbookDialogOpen, setPlaybookDialogOpen] = useState(false);
  const [portfolioDialogOpen, setPortfolioDialogOpen] = useState(false);
  
  // Authority Loop state
  const [assets, setAssets] = useState<any[]>([]);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [portfolioMetrics, setPortfolioMetrics] = useState<any>(null);
  const [selectedIndustryId, setSelectedIndustryId] = useState<string>('');
  
  // Load dashboard
  useEffect(() => {
    loadDashboard();
  }, []);
  
  // Load module data when tab changes
  useEffect(() => {
    loadModuleData(activeTab);
  }, [activeTab]);
  
  const loadDashboard = async () => {
    try {
      setLoading(true);
      const data = await stratagemService.getDashboard();
      setDashboard(data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const loadModuleData = async (tab: number) => {
    try {
      switch (tab) {
        case 0: // Vertical Discovery
          const scores = await stratagemService.getVerticalScores({ limit: 50 });
          setVerticalScores(scores);
          break;
        case 1: // Arbitrage
          const flows = await stratagemService.getActiveFlows({});
          setIntentFlows(flows);
          break;
        case 2: // M&A
          const t = await stratagemService.getTargets({ limit: 50 });
          setTargets(t);
          break;
        case 3: // Authority
          // Will load per-industry assets
          break;
        case 4: // Shadows
          const [inst, pb] = await Promise.all([
            stratagemService.getInstances(),
            stratagemService.getPlaybooks(),
          ]);
          setInstances(inst);
          setPlaybooks(pb);
          break;
        case 5: // Safeguards
          const al = await stratagemService.getAlerts({});
          setAlerts(al);
          break;
      }
    } catch (error) {
      console.error('Failed to load module data:', error);
    }
  };
  
  const handlePromoteToSandbox = async (slug: string) => {
    try {
      await stratagemService.promoteToSandbox([slug]);
      loadModuleData(0);
    } catch (error) {
      console.error('Failed to promote:', error);
    }
  };
  
  const handleLaunchInstance = async (id: string) => {
    try {
      await stratagemService.launchInstance(id);
      loadModuleData(4);
    } catch (error) {
      console.error('Failed to launch instance:', error);
    }
  };
  
  const handleAcknowledgeAlert = async (id: string) => {
    try {
      await stratagemService.acknowledgeAlert(id, 'admin', 'Acknowledged via UI');
      loadModuleData(5);
    } catch (error) {
      console.error('Failed to acknowledge:', error);
    }
  };
  
  // Arbitrage handlers
  const handleDetectSpillover = async () => {
    try {
      const flows = await stratagemService.detectIntentSpillover('default');
      setIntentFlows(flows);
    } catch (error) {
      console.error('Failed to detect spillover:', error);
    }
  };
  
  const [executingFlowId, setExecutingFlowId] = useState<string | null>(null);
  
  const handleExecuteArbitrage = async (flowId: string) => {
    setExecutingFlowId(flowId);
    try {
      await stratagemService.executeArbitrage(flowId, 'execute');
      // Refresh arbitrage data after execution
      await loadModuleData(1);
      alert('Arbitrage executed successfully!');
    } catch (error: any) {
      console.error('Failed to execute arbitrage:', error);
      alert(`Failed to execute: ${error.message || 'Unknown error'}`);
    } finally {
      setExecutingFlowId(null);
    }
  };
  
  // Authority Loop handlers
  const handleAutoIdentifyAssets = async () => {
    if (!selectedIndustryId) {
      alert('Please select an industry first');
      return;
    }
    try {
      const newAssets = await stratagemService.autoIdentifyAssets(selectedIndustryId);
      setAssets(prev => [...prev, ...newAssets]);
    } catch (error) {
      console.error('Failed to auto-identify assets:', error);
    }
  };
  
  const handleCreateAsset = async (assetData: any) => {
    try {
      await stratagemService.createLinkableAsset(assetData);
      setAssetDialogOpen(false);
      if (selectedIndustryId) {
        const updatedAssets = await stratagemService.getAssets(selectedIndustryId);
        setAssets(updatedAssets);
      }
    } catch (error) {
      console.error('Failed to create asset:', error);
    }
  };
  
  const handleCreateCampaign = async (campaignData: any) => {
    try {
      await stratagemService.createCampaign(campaignData);
      setCampaignDialogOpen(false);
      const updatedCampaigns = await stratagemService.getCampaigns();
      setCampaigns(updatedCampaigns);
    } catch (error) {
      console.error('Failed to create campaign:', error);
    }
  };
  
  const handleViewPortfolio = async () => {
    if (!selectedIndustryId) {
      alert('Please select an industry first');
      return;
    }
    try {
      const metrics = await stratagemService.getPortfolioMetrics(selectedIndustryId);
      setPortfolioMetrics(metrics);
      setPortfolioDialogOpen(true);
    } catch (error) {
      console.error('Failed to load portfolio:', error);
    }
  };
  
  // Playbook handlers
  const handleCreatePlaybook = async (playbookData: any) => {
    try {
      await stratagemService.createPlaybook(playbookData);
      setPlaybookDialogOpen(false);
      loadModuleData(4);
    } catch (error) {
      console.error('Failed to create playbook:', error);
    }
  };
  
  // Market Scan Automation
  const [scanRunning, setScanRunning] = useState(false);
  
  const handleRunMarketScan = async () => {
    try {
      setScanRunning(true);
      const result = await stratagemService.runMarketScan();
      // Refresh vertical scores after scan
      const scores = await stratagemService.getVerticalScores();
      setVerticalScores(scores);
      // Refresh intent flows
      const flows = await stratagemService.getActiveFlows();
      setIntentFlows(flows);
      // Refresh dashboard
      loadDashboard();
      alert(`Market scan complete!\n\n✓ ${result.verticalsScanned} verticals discovered\n✓ ${result.seasonalPatterns} seasonal patterns\n✓ ${result.intentFlows} intent flows`);
    } catch (error) {
      console.error('Failed to run market scan:', error);
      alert('Failed to run market scan. Check console for details.');
    } finally {
      setScanRunning(false);
    }
  };
  
  if (loading && !dashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            <PsychologyIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
            Stratagem Nexus
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Autonomous market intelligence • "The system identifies exploitable markets and enters them"
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadDashboard}
        >
          Refresh
        </Button>
      </Box>
      
      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2}>
          <StatsCard
            icon={<RadarIcon />}
            title="Markets Discovered"
            value={dashboard?.verticalDiscovery.topVerticals.length || 0}
            subtitle={`${dashboard?.verticalDiscovery.sandboxCount || 0} in sandbox`}
            color="info.main"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatsCard
            icon={<TrendingUpIcon />}
            title="Arbitrage Opps"
            value={dashboard?.arbitrage.activeOpportunities || 0}
            subtitle={`$${(dashboard?.arbitrage.totalValue || 0).toLocaleString()} value`}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatsCard
            icon={<MergeIcon />}
            title="Targets"
            value={dashboard?.acquisitions.targetsIdentified || 0}
            subtitle={`${dashboard?.acquisitions.targetsAnalyzing || 0} analyzing`}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatsCard
            icon={<CloudIcon />}
            title="Shadow Instances"
            value={dashboard?.shadows.activeInstances || 0}
            subtitle={`${dashboard?.shadows.aggregatedLearnings || 0} learnings`}
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatsCard
            icon={<HubIcon />}
            title="Playbooks"
            value={dashboard?.shadows.playbooksDeployed || 0}
            subtitle="Active deployments"
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatsCard
            icon={<WarningIcon />}
            title="Alerts"
            value={dashboard?.safeguards.activeAlerts || 0}
            subtitle={`${dashboard?.safeguards.criticalAlerts || 0} critical`}
            color={dashboard?.safeguards.criticalAlerts ? 'error.main' : 'success.main'}
          />
        </Grid>
      </Grid>
      
      {/* Quick Actions Panel - ONE CLICK POWER */}
      <Paper sx={{ 
        p: 2, 
        mb: 3, 
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
      }}>
        <Typography variant="subtitle2" color="primary.main" fontWeight={600} gutterBottom>
          ⚡ QUICK ACTIONS — One-Click Power
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="success"
            startIcon={<TrendingUpIcon />}
            onClick={handleDetectSpillover}
          >
            Detect Opportunities
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<RadarIcon />}
            onClick={handleAutoIdentifyAssets}
            disabled={!selectedIndustryId}
          >
            Auto-Identify Assets
          </Button>
          <Button
            variant="outlined"
            startIcon={<CloudIcon />}
            onClick={() => setInstanceDialogOpen(true)}
          >
            Launch Shadow Instance
          </Button>
          <Button
            variant="outlined"
            startIcon={<WarningIcon />}
            onClick={() => {
              setActiveTab(5);
              loadModuleData(5);
            }}
          >
            Review Alerts ({dashboard?.safeguards.criticalAlerts || 0} Critical)
          </Button>
        </Box>
      </Paper>
      
      {/* Module Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab
            icon={<RadarIcon />}
            iconPosition="start"
            label="Vertical Discovery"
          />
          <Tab
            icon={<AutoGraphIcon />}
            iconPosition="start"
            label="Arbitrage"
          />
          <Tab
            icon={<MergeIcon />}
            iconPosition="start"
            label="M&A Ontology"
          />
          <Tab
            icon={<LinkIcon />}
            iconPosition="start"
            label="Authority Loop"
          />
          <Tab
            icon={<CloudIcon />}
            iconPosition="start"
            label="Shadows"
          />
          <Tab
            icon={<SecurityIcon />}
            iconPosition="start"
            label="Safeguards"
            sx={{
              '& .MuiBadge-badge': { right: -8 }
            }}
          />
          <Tab
            icon={<TrendingUpIcon />}
            iconPosition="start"
            label="Federal Capture"
          />
          <Tab
            icon={<FlywheelIcon />}
            iconPosition="start"
            label="Sales Flywheel"
            sx={{ color: 'success.main' }}
          />
        </Tabs>
        
        <CardContent>
          {/* Tab 0: Vertical Discovery */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">
                VAS Scoring: (Economic Value × Fragmentation) ÷ (Content Quality × Compliance Friction) × Learning Velocity
              </Typography>
              <Button
                variant="contained"
                startIcon={<ScienceIcon />}
                onClick={() => stratagemService.runSandboxEvaluation()}
              >
                Run Sandbox Evaluation
              </Button>
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Vertical</TableCell>
                    <TableCell align="center">VAS Score</TableCell>
                    <TableCell align="center">Economic Value</TableCell>
                    <TableCell align="center">Fragmentation</TableCell>
                    <TableCell align="center">Content Quality</TableCell>
                    <TableCell align="center">Compliance</TableCell>
                    <TableCell align="center">Learning V.</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {verticalScores.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Box sx={{ py: 4 }}>
                          <RadarIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Verticals Discovered
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 2 }}>
                            Run a market scan to populate Vertical Discovery with real industry data including 
                            VAS (Vertical Attractiveness Score) metrics, seasonal patterns, and arbitrage opportunities.
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={scanRunning ? <CircularProgress size={18} color="inherit" /> : <RocketIcon />}
                              onClick={handleRunMarketScan}
                              disabled={scanRunning}
                            >
                              {scanRunning ? 'Scanning Markets...' : 'Run Market Scan'}
                            </Button>
                            <Button
                              variant="outlined"
                              href="/attention-engine"
                            >
                              Configure Manual Scrapers
                            </Button>
                          </Box>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
                            Market scan analyzes 12 industry verticals including home services, legal, solar, medical, and more.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    verticalScores.map((v: any) => (
                      <TableRow key={v.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>{v.vertical_slug || v.verticalSlug}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <VasScore score={v.vas_score ?? v.vasScore ?? 0} />
                        </TableCell>
                        <TableCell align="center">{(v.economic_value ?? v.economicValue ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="center">{(v.fragmentation ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="center">{(v.content_quality ?? v.contentQuality ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="center">{(v.compliance_friction ?? v.complianceFriction ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="center">{(v.learning_velocity ?? v.learningVelocity ?? 0).toFixed(2)}</TableCell>
                        <TableCell align="center">
                          <StatusBadge status={v.status} />
                        </TableCell>
                        <TableCell align="center">
                          {v.status === 'discovered' && (
                            <Tooltip title="Promote to Sandbox">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handlePromoteToSandbox(v.vertical_slug || v.verticalSlug)}
                              >
                                <ScienceIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {v.status === 'sandbox' && (
                            <Tooltip title="Promote to Industry">
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => stratagemService.createIndustryFromVertical(v.vertical_slug || v.verticalSlug)}
                              >
                                <RocketIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          
          {/* Tab 1: Arbitrage */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Cross-Market Attention Arbitrage</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleDetectSpillover}
              >
                Detect Spillover
              </Button>
            </Box>
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Intent Spillover
                    </Typography>
                    <Typography variant="body2">
                      Auto-syndicate top-performing content from one vertical to adjacent markets
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Seasonal Arbitrage
                    </Typography>
                    <Typography variant="body2">
                      Pre-index content weeks before seasonal demand spikes
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Regulatory Arbitrage
                    </Typography>
                    <Typography variant="body2">
                      Route content through lowest-friction compliance regions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Source Market</TableCell>
                    <TableCell>Target Market</TableCell>
                    <TableCell>Intent Type</TableCell>
                    <TableCell align="center">Flow Strength</TableCell>
                    <TableCell align="center">Arbitrage Value</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {intentFlows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Box sx={{ py: 4 }}>
                          <AutoGraphIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Intent Flows Detected
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 2 }}>
                            Intent flows capture cross-market spillover opportunities where customer intent in one 
                            market creates arbitrage potential in related markets.
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                            <Button
                              variant="contained"
                              color="primary"
                              startIcon={scanRunning ? <CircularProgress size={18} color="inherit" /> : <RocketIcon />}
                              onClick={handleRunMarketScan}
                              disabled={scanRunning}
                            >
                              {scanRunning ? 'Scanning...' : 'Run Market Scan'}
                            </Button>
                          </Box>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
                            Market scan creates intent flows for pool→home-services, solar→home-services, and legal→real-estate patterns.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    intentFlows.map((flow: any) => (
                      <TableRow key={flow.id} hover>
                        <TableCell>{flow.source_market || flow.sourceMarket}</TableCell>
                        <TableCell>{flow.target_market || flow.targetMarket}</TableCell>
                        <TableCell>
                          <Chip label={flow.intent_type || flow.intentType} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="center">
                          <LinearProgress
                            variant="determinate"
                            value={(flow.flow_strength ?? flow.flowStrength ?? 0) * 100}
                            sx={{ width: 80, mx: 'auto' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          ${(flow.arbitrage_value ?? flow.arbitrageValue ?? 0).toLocaleString()}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={(flow.is_active ?? flow.isActive) ? 'Active' : 'Inactive'}
                            color={(flow.is_active ?? flow.isActive) ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={executingFlowId === flow.id}
                            onClick={() => handleExecuteArbitrage(flow.id)}
                          >
                            {executingFlowId === flow.id ? 'Executing...' : 'Execute'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          
          {/* Tab 2: M&A Ontology */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Acquisition Targets & Ontology Ingestion</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setTargetDialogOpen(true)}
              >
                Identify Target
              </Button>
            </Box>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Acquisition Score Formula:</strong> Content Richness (0.25) + Optimization Gap (0.20) + Trust Score (0.30) + System Weakness (0.10) + Growth Stagnation (0.15)
              </Typography>
            </Alert>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Domain</TableCell>
                    <TableCell>Vertical</TableCell>
                    <TableCell>Site Type</TableCell>
                    <TableCell align="center">Acquisition Score</TableCell>
                    <TableCell align="center">Est. Value</TableCell>
                    <TableCell align="center">Priority</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {targets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Box sx={{ py: 4 }}>
                          <MergeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No Acquisition Targets
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto', mb: 2 }}>
                            M&A Ontology identifies competitor domains with weak content, optimization gaps, 
                            or stagnant growth that represent acquisition opportunities.
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            startIcon={<AddIcon />}
                            onClick={() => setTargetDialogOpen(true)}
                          >
                            Add Target Domain
                          </Button>
                          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
                            Analyze competitor sites to calculate acquisition scores based on content richness, trust, and SEO gaps.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    targets.map((target) => (
                      <TableRow key={target.id} hover>
                        <TableCell>
                          <Typography fontWeight={600}>{target.domain}</Typography>
                        </TableCell>
                        <TableCell>{target.vertical_slug}</TableCell>
                        <TableCell>{target.site_type}</TableCell>
                        <TableCell align="center">
                          <VasScore score={target.acquisition_score} />
                        </TableCell>
                        <TableCell align="center">
                          ${target.estimated_value.toLocaleString()}
                        </TableCell>
                        <TableCell align="center">{target.ingestion_priority}</TableCell>
                        <TableCell align="center">
                          <StatusBadge status={target.status} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {target.status === 'identified' && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => stratagemService.analyzeTarget(target.id)}
                              >
                                Analyze
                              </Button>
                            )}
                            {target.status === 'approved' && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                              >
                                Ingest
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          
          {/* Tab 3: Authority Loop */}
          <TabPanel value={activeTab} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Autonomous Authority Loop</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAssetDialogOpen(true)}
              >
                Create Linkable Asset
              </Button>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                        <BubbleChartIcon />
                      </Avatar>
                      <Typography variant="h6">Linkable Assets</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Data studies, tools, calculators, infographics, guides, and templates
                      that naturally attract backlinks.
                    </Typography>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleAutoIdentifyAssets}
                    >
                      Auto-Identify Assets
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'success.main', mr: 1 }}>
                        <AnalyticsIcon />
                      </Avatar>
                      <Typography variant="h6">Outreach Campaigns</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Automated outreach to build links. Track sends, responses,
                      and secured placements.
                    </Typography>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setCampaignDialogOpen(true)}
                    >
                      Create Campaign
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'warning.main', mr: 1 }}>
                        <AccountTreeIcon />
                      </Avatar>
                      <Typography variant="h6">Link Portfolio</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Track all links, monitor ROI, and retire low-impact placements
                      automatically.
                    </Typography>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleViewPortfolio}
                    >
                      View Portfolio
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Tab 4: Shadows */}
          <TabPanel value={activeTab} index={4}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Shadow Deployments — "One Brain, Many Masks"</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setInstanceDialogOpen(true)}
              >
                Create Instance
              </Button>
            </Box>
            
            <Grid container spacing={3}>
              {/* Instances */}
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Active Instances
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Instance</TableCell>
                        <TableCell>Domain</TableCell>
                        <TableCell>Vertical</TableCell>
                        <TableCell align="center">Learning Score</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {instances.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Box sx={{ py: 4 }}>
                              <CloudIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                              <Typography variant="h6" color="text.secondary" gutterBottom>
                                No Shadow Instances
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 450, mx: 'auto', mb: 2 }}>
                                Shadow deployments allow running parallel campaigns across multiple domains 
                                with shared learning while maintaining distinct brand identities.
                              </Typography>
                              <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => setInstanceDialogOpen(true)}
                              >
                                Create Shadow Instance
                              </Button>
                              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
                                "One Brain, Many Masks" — insights flow across instances while brands stay isolated.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        instances.map((inst) => (
                          <TableRow key={inst.id} hover>
                            <TableCell>
                              <Typography fontWeight={600}>{inst.instance_name}</Typography>
                            </TableCell>
                            <TableCell>{inst.domain}</TableCell>
                            <TableCell>{inst.vertical_slug}</TableCell>
                            <TableCell align="center">
                              <VasScore score={inst.learning_contribution_score} />
                            </TableCell>
                            <TableCell align="center">
                              <StatusBadge status={inst.status} />
                            </TableCell>
                            <TableCell>
                              {inst.status === 'provisioning' && (
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => handleLaunchInstance(inst.id)}
                                >
                                  <PlayIcon />
                                </IconButton>
                              )}
                              {inst.status === 'active' && (
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => stratagemService.pauseInstance(inst.id)}
                                >
                                  <PauseIcon />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              
              {/* Playbooks */}
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Playbooks
                </Typography>
                <Card variant="outlined">
                  <Box sx={{ p: 2 }}>
                    {playbooks.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <HubIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                          No Playbooks
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
                          Playbooks define reusable campaign strategies that can be deployed across shadow instances.
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setPlaybookDialogOpen(true)}
                        >
                          Create Playbook
                        </Button>
                      </Box>
                    ) : (
                      playbooks.map((pb) => (
                        <Box
                          key={pb.id}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography fontWeight={600}>{pb.playbook_name}</Typography>
                            <StatusBadge status={pb.status} />
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            v{pb.version} • {pb.deployments_count} deployments • {(pb.success_rate * 100).toFixed(0)}% success
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Tab 5: Safeguards */}
          <TabPanel value={activeTab} index={5}>
            <Typography variant="h6" gutterBottom>Control & Safeguards</Typography>
            
            <Grid container spacing={3}>
              {/* Velocity Caps */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <SpeedIcon sx={{ mr: 1, color: 'warning.main' }} />
                      <Typography variant="h6">Velocity Caps</Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Resource</TableCell>
                            <TableCell align="center">Hourly</TableCell>
                            <TableCell align="center">Daily</TableCell>
                            <TableCell align="center">Weekly</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>Content</TableCell>
                            <TableCell align="center">5</TableCell>
                            <TableCell align="center">50</TableCell>
                            <TableCell align="center">200</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Pages</TableCell>
                            <TableCell align="center">10</TableCell>
                            <TableCell align="center">100</TableCell>
                            <TableCell align="center">500</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Links</TableCell>
                            <TableCell align="center">3</TableCell>
                            <TableCell align="center">20</TableCell>
                            <TableCell align="center">100</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell>Outreach</TableCell>
                            <TableCell align="center">10</TableCell>
                            <TableCell align="center">50</TableCell>
                            <TableCell align="center">250</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>
              
              {/* Alerts */}
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <WarningIcon sx={{ mr: 1, color: 'error.main' }} />
                      <Typography variant="h6">Anomaly Alerts</Typography>
                    </Box>
                    {alerts.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 3 }}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                        <Typography color="success.main">All systems nominal</Typography>
                      </Box>
                    ) : (
                      alerts.slice(0, 5).map((alert) => (
                        <Box
                          key={alert.id}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            borderRadius: 1,
                            bgcolor: alpha(
                              alert.severity === 'critical'
                                ? theme.palette.error.main
                                : alert.severity === 'high'
                                ? theme.palette.warning.main
                                : theme.palette.info.main,
                              0.1
                            ),
                            border: 1,
                            borderColor: alert.severity === 'critical' ? 'error.main' : 'divider',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <StatusBadge status={alert.severity} />
                                <Typography fontWeight={600}>{alert.alert_type}</Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {alert.description}
                              </Typography>
                            </Box>
                            {!alert.is_acknowledged && (
                              <IconButton
                                size="small"
                                onClick={() => handleAcknowledgeAlert(alert.id)}
                              >
                                <CheckCircleIcon />
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                      ))
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Tab 6: Federal Capture */}
          <TabPanel value={activeTab} index={6}>
            <FederalCaptureTab />
          </TabPanel>
          
          {/* Tab 7: Sales Flywheel - Opportunity State Machine */}
          <TabPanel value={activeTab} index={7}>
            <SalesFlywheelTab />
          </TabPanel>
        </CardContent>
      </Card>
      
      {/* Market Scan Info Dialog - Data comes from Attention Engine scraping */}
      <Dialog open={scanDialogOpen} onClose={() => setScanDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Market Vertical Discovery</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Vertical discovery data is populated automatically by the Attention Engine's market scanning operations.
            </Alert>
            <Typography variant="body1" gutterBottom>
              <strong>How Vertical Discovery Works:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2 }}>
              <li>The Attention Engine scrapes SERP data, CPC, and competition metrics</li>
              <li>VAS (Vertical Attractiveness Score) is calculated automatically</li>
              <li>High-scoring verticals are promoted to sandbox testing</li>
              <li>Successful verticals become full industries in the system</li>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Configure industry scrapers and scoring thresholds in the Attention Engine settings.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanDialogOpen(false)}>Close</Button>
          <Button
            variant="contained"
            href="/attention-engine"
          >
            Go to Attention Engine
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Launch Shadow Instance Dialog */}
      <Dialog open={instanceDialogOpen} onClose={() => setInstanceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Launch New Shadow Instance</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField 
              label="Instance Name" 
              fullWidth 
              id="instance-name"
              placeholder="e.g., Tampa Home Services"
            />
            <TextField 
              label="Domain" 
              fullWidth 
              id="instance-domain"
              placeholder="e.g., tampa-home-services.com"
            />
            <TextField 
              label="Vertical Slug" 
              fullWidth 
              id="instance-vertical"
              placeholder="e.g., home-services"
            />
            <TextField 
              label="Brand Config (JSON)" 
              fullWidth 
              multiline
              rows={4}
              id="instance-brand"
              defaultValue='{"primary_color": "#0066cc", "tagline": "Your trusted local service"}'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInstanceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const instanceName = (document.getElementById('instance-name') as HTMLInputElement)?.value;
              const domain = (document.getElementById('instance-domain') as HTMLInputElement)?.value;
              const verticalSlug = (document.getElementById('instance-vertical') as HTMLInputElement)?.value;
              const brandConfigStr = (document.getElementById('instance-brand') as HTMLTextAreaElement)?.value;
              let brandConfig = {};
              try { brandConfig = JSON.parse(brandConfigStr || '{}'); } catch (e) {}
              
              if (!instanceName || !domain) {
                alert('Please enter instance name and domain');
                return;
              }
              
              try {
                await stratagemService.createInstance({
                  instance_name: instanceName,
                  domain,
                  vertical_slug: verticalSlug || undefined,
                  brand_config: brandConfig,
                });
                setInstanceDialogOpen(false);
                loadModuleData(4);
              } catch (err) {
                console.error('Failed to create instance:', err);
                alert('Failed to create shadow instance');
              }
            }}
          >
            Create Instance
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Add M&A Target Dialog */}
      <Dialog open={targetDialogOpen} onClose={() => setTargetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Identify Acquisition Target</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField 
              label="Domain" 
              fullWidth 
              id="target-domain"
              placeholder="e.g., competitor-site.com"
            />
            <TextField 
              label="Vertical Slug" 
              fullWidth 
              id="target-vertical"
              placeholder="e.g., home-services"
            />
            <TextField
              select
              label="Site Type"
              fullWidth
              defaultValue="content-site"
              SelectProps={{ native: true }}
              id="target-type"
            >
              <option value="content-site">Content Site</option>
              <option value="directory">Directory</option>
              <option value="marketplace">Marketplace</option>
              <option value="saas">SaaS Tool</option>
            </TextField>
            <TextField 
              label="Domain Authority" 
              fullWidth 
              type="number"
              id="target-da"
              inputProps={{ min: 0, max: 100 }}
            />
            <TextField 
              label="Estimated Monthly Traffic" 
              fullWidth 
              type="number"
              id="target-traffic"
            />
            <TextField 
              label="Notes" 
              fullWidth 
              multiline
              rows={2}
              id="target-notes"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTargetDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const domain = (document.getElementById('target-domain') as HTMLInputElement)?.value;
              const verticalSlug = (document.getElementById('target-vertical') as HTMLInputElement)?.value;
              const siteType = (document.getElementById('target-type') as HTMLSelectElement)?.value;
              const da = parseInt((document.getElementById('target-da') as HTMLInputElement)?.value || '0');
              const traffic = parseInt((document.getElementById('target-traffic') as HTMLInputElement)?.value || '0');
              const notes = (document.getElementById('target-notes') as HTMLTextAreaElement)?.value;
              
              if (!domain || !verticalSlug) {
                alert('Please enter domain and vertical slug');
                return;
              }
              
              try {
                await stratagemService.identifyTarget({
                  domain,
                  vertical_slug: verticalSlug,
                  site_type: siteType,
                  metrics: { da, traffic },
                  notes,
                });
                setTargetDialogOpen(false);
                loadModuleData(2);
              } catch (err) {
                console.error('Failed to identify target:', err);
                alert('Failed to identify target');
              }
            }}
          >
            Add Target
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={assetDialogOpen} onClose={() => setAssetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Linkable Asset</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Industry ID"
              fullWidth
              value={selectedIndustryId}
              onChange={(e) => setSelectedIndustryId(e.target.value)}
              helperText="Enter the target industry ID"
            />
            <TextField
              select
              label="Asset Type"
              fullWidth
              defaultValue="guide"
              SelectProps={{ native: true }}
              id="asset-type"
            >
              <option value="data_study">Data Study</option>
              <option value="tool">Tool</option>
              <option value="calculator">Calculator</option>
              <option value="infographic">Infographic</option>
              <option value="guide">Guide</option>
              <option value="template">Template</option>
            </TextField>
            <TextField label="Title" fullWidth id="asset-title" />
            <TextField label="URL" fullWidth id="asset-url" />
            <TextField label="Description" fullWidth multiline rows={3} id="asset-description" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssetDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const assetType = (document.getElementById('asset-type') as HTMLSelectElement)?.value;
              const title = (document.getElementById('asset-title') as HTMLInputElement)?.value;
              const url = (document.getElementById('asset-url') as HTMLInputElement)?.value;
              const description = (document.getElementById('asset-description') as HTMLTextAreaElement)?.value;
              handleCreateAsset({ industryId: selectedIndustryId, assetType, title, url, description });
            }}
          >
            Create Asset
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Create Campaign Dialog */}
      <Dialog open={campaignDialogOpen} onClose={() => setCampaignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Outreach Campaign</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Campaign Name" fullWidth id="campaign-name" />
            <TextField label="Asset ID" fullWidth id="campaign-asset-id" helperText="ID of the linkable asset to promote" />
            <TextField 
              label="Target Sites" 
              fullWidth 
              multiline 
              rows={3} 
              id="campaign-targets"
              helperText="One domain per line (e.g., example.com)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const campaignName = (document.getElementById('campaign-name') as HTMLInputElement)?.value;
              const assetId = (document.getElementById('campaign-asset-id') as HTMLInputElement)?.value;
              const targets = (document.getElementById('campaign-targets') as HTMLTextAreaElement)?.value;
              const targetSites = targets?.split('\n').filter(t => t.trim()).map(domain => ({ domain: domain.trim(), relevance: 0.8 }));
              handleCreateCampaign({ assetId, campaignName, targetSites });
            }}
          >
            Create Campaign
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Create Playbook Dialog */}
      <Dialog open={playbookDialogOpen} onClose={() => setPlaybookDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Playbook</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Playbook Name" fullWidth id="playbook-name" />
            <TextField label="Vertical Slug" fullWidth id="playbook-vertical" helperText="Optional: target vertical (e.g., home-services)" />
            <TextField 
              label="Content Patterns" 
              fullWidth 
              multiline 
              rows={3} 
              id="playbook-patterns"
              helperText="One pattern per line (e.g., service-area, service-type)"
            />
            <TextField 
              label="Strategies (JSON)" 
              fullWidth 
              multiline 
              rows={4} 
              id="playbook-strategies"
              defaultValue='[{"name": "Local SEO", "priority": 1}]'
              helperText="JSON array of strategy objects"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlaybookDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              const playbookName = (document.getElementById('playbook-name') as HTMLInputElement)?.value;
              const verticalSlug = (document.getElementById('playbook-vertical') as HTMLInputElement)?.value;
              const patterns = (document.getElementById('playbook-patterns') as HTMLTextAreaElement)?.value;
              const strategiesStr = (document.getElementById('playbook-strategies') as HTMLTextAreaElement)?.value;
              const contentPatterns = patterns?.split('\n').filter(p => p.trim());
              let strategies = [];
              try { strategies = JSON.parse(strategiesStr || '[]'); } catch (e) {}
              handleCreatePlaybook({ playbookName, verticalSlug: verticalSlug || undefined, contentPatterns, strategies });
            }}
          >
            Create Playbook
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Portfolio Metrics Dialog */}
      <Dialog open={portfolioDialogOpen} onClose={() => setPortfolioDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Link Portfolio Metrics</DialogTitle>
        <DialogContent>
          {portfolioMetrics ? (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                      {portfolioMetrics.totalLinks}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Total Links</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h4" fontWeight={700} color="success.main">
                      {portfolioMetrics.liveLinks}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Live Links</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h4" fontWeight={700} color="info.main">
                      {portfolioMetrics.avgDomainAuthority?.toFixed(1) || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Avg DA</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} md={3}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h4" fontWeight={700} color="warning.main">
                      ${portfolioMetrics.totalTrafficValue?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Traffic Value</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Top Domains</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {portfolioMetrics.topDomains?.map((domain: string) => (
                    <Chip key={domain} label={domain} size="small" />
                  ))}
                </Box>
              </Grid>
            </Grid>
          ) : (
            <Typography color="text.secondary">Loading metrics...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPortfolioDialogOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={async () => {
              if (selectedIndustryId) {
                await stratagemService.retireLowImpactLinks(selectedIndustryId, 0.5);
                const metrics = await stratagemService.getPortfolioMetrics(selectedIndustryId);
                setPortfolioMetrics(metrics);
              }
            }}
          >
            Retire Low ROI Links
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StratagemNexus;
